import React, { useState } from 'react';
import { ArrowLeft, BarChart3, TrendingUp, Users, Calculator, MessageCircle, PieChart, Target, Phone, Mail, MessageSquare, FileText, Settings, CreditCard, Bell, LogOut, User, Shield, Sparkles } from 'lucide-react';

interface MonComptePageProps {
  onNavigateBack: () => void;
}

const MonComptePage: React.FC<MonComptePageProps> = ({ onNavigateBack }) => {
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <button
                onClick={onNavigateBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Retour</span>
              </button>
              <div className="ml-6 flex items-center space-x-3">
                <div className="service-icon-teal">
                  <PieChart className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold hero-title-gradient">
                  SOeasi
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <span className="text-sm text-gray-600">Mon Compte</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Mon Espace</h3>
                <p className="text-sm text-gray-600">Gérez vos investissements et votre profil</p>
              </div>
              <nav className="space-y-1">
                <a href="#" className="flex items-center space-x-3 px-4 py-3 rounded-xl bg-gradient-to-r from-teal-50 to-blue-50 text-teal-700 font-semibold transition-all duration-200">
                  <BarChart3 className="w-5 h-5" />
                  <span>Tableau de Bord</span>
                </a>
                <a href="#" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all duration-200">
                  <TrendingUp className="w-5 h-5" />
                  <span>Analyses</span>
                </a>
                <a href="#" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all duration-200">
                  <FileText className="w-5 h-5" />
                  <span>Recommandations</span>
                </a>
                <a href="#" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all duration-200">
                  <Users className="w-5 h-5" />
                  <span>Comparateur</span>
                </a>
                <a href="#" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all duration-200">
                  <Calculator className="w-5 h-5" />
                  <span>Calculateur</span>
                </a>
                <a href="#" className="flex items-center space-x-3 px-4 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-blue-500 text-white font-semibold shadow-lg transform hover:scale-105 transition-all duration-200">
                  <Target className="w-5 h-5" />
                  <span>Investir</span>
                </a>
                
                <div className="border-t border-gray-200 my-4 pt-4">
                  <p className="text-xs text-gray-500 mb-3 uppercase font-semibold tracking-wide">Paramètres</p>
                </div>
                
                <a href="#" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all duration-200">
                  <Settings className="w-5 h-5" />
                  <span>Paramètres</span>
                </a>
                <a href="#" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all duration-200">
                  <CreditCard className="w-5 h-5" />
                  <span>Paiements</span>
                </a>
                <a href="#" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all duration-200">
                  <Bell className="w-5 h-5" />
                  <span>Notifications</span>
                </a>
                <a href="#" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 transition-all duration-200">
                  <LogOut className="w-5 h-5" />
                  <span>Déconnexion</span>
                </a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Investment Process Header */}
              <div className="p-8 bg-gradient-to-r from-teal-500 to-blue-500 text-white relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/5 rounded-full -ml-10 -mb-10"></div>
                
                <div className="relative">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <Target className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-white/40 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-white/60 rounded-full animate-pulse delay-150"></div>
                      <div className="w-2 h-2 bg-white/40 rounded-full animate-pulse delay-300"></div>
                    </div>
                  </div>
                  
                  <h2 className="text-3xl font-bold mb-3">Votre parcours d'investissement personnalisé</h2>
                  <p className="text-white/90 text-lg mb-4">
                    Nous vous accompagnons étape par étape pour construire votre avenir financier selon vos objectifs.
                  </p>
                  
                  <div className="flex items-center space-x-6 text-sm">
                    <div className="flex items-center space-x-2 bg-white/20 rounded-full px-3 py-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span>100% sécurisé</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span>⏱️</span>
                      <span>~15 minutes</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span>💬</span>
                      <span>Support en direct</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Steps */}
              <div className="p-8 bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm mb-2">
                      1
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-sm text-gray-900">Sélection</div>
                      <div className="text-xs text-gray-500">Choix du produit</div>
                    </div>
                  </div>
                  
                  <div className="flex-1 h-2 bg-gray-200 mx-6 rounded-full overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-teal-500 to-blue-500 w-1/4 rounded-full"></div>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold text-sm mb-2">
                      2
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-sm text-gray-600">Montant</div>
                      <div className="text-xs text-gray-500">Définition du montant</div>
                    </div>
                  </div>
                  
                  <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold text-sm mb-2">
                      3
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-sm text-gray-600">Vérification</div>
                      <div className="text-xs text-gray-500">Conformité et documents</div>
                    </div>
                  </div>
                  
                  <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold text-sm mb-2">
                      4
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-sm text-gray-600">Paiement</div>
                      <div className="text-xs text-gray-500">Traitement sécurisé</div>
                    </div>
                  </div>
                  
                  <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold text-sm mb-2">
                      5
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-sm text-gray-600">Confirmation</div>
                      <div className="text-xs text-gray-500">Finalisation</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Selection */}
              <div className="p-8">
                <div className="mb-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg flex items-center justify-center">
                      <Target className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Sélection du produit d'investissement</h3>
                  </div>
                  <p className="text-gray-600 text-lg">Choisissez le produit d'investissement qui correspond à votre profil et vos objectifs.</p>
                </div>

                {/* Investment Products */}
                <div className="space-y-6">
                  {/* Sukuk Infrastructure Durable */}
                  <div className="group bg-gradient-to-br from-white to-green-50 border-2 border-gray-100 rounded-2xl p-6 hover:border-green-300 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <Shield className="w-8 h-8 text-green-600" />
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900 mb-1">Sukuk Infrastructure Durable</h4>
                          <p className="text-sm text-green-600 font-medium">Sukuk Gouvernemental</p>
                        </div>
                      </div>
                      <div className="text-right bg-white rounded-xl p-4 shadow-sm">
                        <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">3.5%</div>
                        <div className="text-sm text-gray-500">par an</div>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      Obligations islamiques adossées à des projets d'infrastructure durable et éthique.
                    </p>
                    
                    <div className="flex items-center space-x-8 mb-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-700">3 ans</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-green-600">Risque Faible</span>
                      </div>
                      <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-1 shadow-sm">
                        <span className="text-sm font-bold text-green-600">€</span>
                        <span className="text-sm font-medium text-gray-700">Min. 50 €</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <span className="px-4 py-2 bg-gradient-to-r from-green-100 to-teal-100 text-green-700 rounded-full text-sm font-medium">Finance éthique</span>
                      <span className="px-4 py-2 bg-gradient-to-r from-teal-100 to-blue-100 text-teal-700 rounded-full text-sm font-medium">Infrastructure durable</span>
                      <span className="px-4 py-2 bg-gradient-to-r from-blue-100 to-green-100 text-blue-700 rounded-full text-sm font-medium">Accessible</span>
                    </div>
                  </div>

                  {/* SCPI Education Santé */}
                  <div className="group bg-gradient-to-br from-white to-teal-50 border-2 border-gray-100 rounded-2xl p-6 hover:border-teal-300 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <FileText className="w-8 h-8 text-teal-600" />
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900 mb-1">SCPI Education Santé</h4>
                          <p className="text-sm text-teal-600 font-medium">SCPI Résidentiel</p>
                        </div>
                      </div>
                      <div className="text-right bg-white rounded-xl p-4 shadow-sm">
                        <div className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">4.2%</div>
                        <div className="text-sm text-gray-500">par an</div>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      Investissement dans l'immobilier résidentiel européen avec un portefeuille diversifié de biens locatifs.
                    </p>
                    
                    <div className="flex items-center space-x-8 mb-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-700">8 ans</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-orange-600">Risque Modéré</span>
                      </div>
                      <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-1 shadow-sm">
                        <span className="text-sm font-bold text-teal-600">€</span>
                        <span className="text-sm font-medium text-gray-700">Min. 1 010 €</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <span className="px-4 py-2 bg-gradient-to-r from-teal-100 to-blue-100 text-teal-700 rounded-full text-sm font-medium">Revenus réguliers</span>
                      <span className="px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-full text-sm font-medium">Diversification géographique</span>
                      <span className="px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-medium">Gestion professionnelle</span>
                    </div>
                  </div>

                  {/* GFI label ISR */}
                  <div className="group bg-gradient-to-br from-white to-blue-50 border-2 border-gray-100 rounded-2xl p-6 hover:border-blue-300 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <TrendingUp className="w-8 h-8 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900 mb-1">GFI Label ISR</h4>
                          <p className="text-sm text-blue-600 font-medium">GFI Croissance</p>
                        </div>
                      </div>
                      <div className="text-right bg-white rounded-xl p-4 shadow-sm">
                        <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">4.8%</div>
                        <div className="text-sm text-gray-500">par an</div>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      Fonds d'investissement spécialisé dans les entreprises technologiques innovantes en phase de croissance.
                    </p>
                    
                    <div className="flex items-center space-x-8 mb-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-700">8 ans</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-red-600">Risque Modéré</span>
                      </div>
                      <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-1 shadow-sm">
                        <span className="text-sm font-bold text-blue-600">€</span>
                        <span className="text-sm font-medium text-gray-700">Min. 1 010 €</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <span className="px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-full text-sm font-medium">Potentiel de croissance élevé</span>
                      <span className="px-4 py-2 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-full text-sm font-medium">Secteur technologique</span>
                      <span className="px-4 py-2 bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-700 rounded-full text-sm font-medium">Gestion active</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Help Section */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* No Product Selected */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center overflow-hidden relative">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-teal-500 to-blue-500"></div>
                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6 transform transition-transform duration-300 hover:scale-110">
                  <Target className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Aucun produit sélectionné</h3>
                <p className="text-gray-600 leading-relaxed">Choisissez un produit d'investissement pour continuer votre processus.</p>
                <div className="mt-6 p-4 bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl">
                  <div className="flex items-center space-x-2 text-teal-600">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">3 produits disponibles</span>
                  </div>
                </div>
              </div>

              {/* Help Section */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-teal-500/10 to-blue-500/10 rounded-full -mr-12 -mt-12"></div>
                <div className="relative">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-blue-500 rounded-xl flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Besoin d'aide ?</h3>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl">
                      <Phone className="w-5 h-5 text-teal-600" />
                      <span className="text-gray-700 font-medium">+33 1 23 45 67 89</span>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-teal-50 rounded-xl">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-700 font-medium">support@SOeasi.fr</span>
                    </div>
                  </div>
                  
                  <button className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-teal-500 to-blue-500 text-white py-3 px-4 rounded-xl hover:from-teal-600 hover:to-blue-600 transition-all duration-300 shadow-lg transform hover:-translate-y-1">
                    <MessageSquare className="w-5 h-5" />
                    <span className="font-semibold">Chat en direct</span>
                  </button>
                </div>
              </div>

              {/* Security Notice */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-blue-900 mb-2 flex items-center">
                      Investissement sécurisé
                      <div className="ml-2 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    </h4>
                    <p className="text-sm text-blue-800 leading-relaxed">
                      Vos données sont protégées par un chiffrement de niveau bancaire et 
                      nous sommes régulés par l'AMF.
                    </p>
                    <div className="mt-3 flex items-center space-x-2">
                      <div className="text-xs bg-white text-blue-600 px-2 py-1 rounded-full font-medium">AMF</div>
                      <div className="text-xs bg-white text-green-600 px-2 py-1 rounded-full font-medium">SSL</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonComptePage;